import React, { useCallback, useState } from 'react'

import { Button, ButtonVariants } from '@inno-ui/button'
import {
  CheckmarkIcon,
  IconSizes,
  LockShieldLineIcon,
  QrCodeViewfinderLineIcon
} from '@inno-ui/icons'
import { Modal } from '@inno-ui/modal'
import { Spinner } from '@inno-ui/spinner'
import { toast } from '@inno-ui/toast'
import cn from 'classnames'
import { OtpInput } from 'modules/auth/components/OtpInput'
import { useTranslation } from 'react-i18next'
import { useEnableTwoFactor } from 'shared/api/auth/requests/2fa/enableTwoFactor'
import { useGetQrCode } from 'shared/api/auth/requests/2fa/getQrCode'

import style from './EnableTwoFactorModal.module.scss'
import { EnableTwoFactorModalProps } from './EnableTwoFactorModal.types'
import apple from './images/apple_badge.svg'
import google from './images/google_play_badge.svg'
import svg1 from './images/svg1.svg'
import svg2 from './images/svg2.svg'

export const EnableTwoFactorModal: React.FC<EnableTwoFactorModalProps> = ({
  isOpen,
  setIsOpen,
  onSuccess: onSuccessToggle,
  onCancel
}) => {
  const { t } = useTranslation('auth', {
    keyPrefix: 'enable_two_factor_modal'
  })
  const [otp, setOtp] = useState('')
  const [otpActivated, setOtpActivated] = useState(false)
  const [isError, setIsError] = useState(false)

  const handleCLoseModal = () => {
    setIsError(false)
    setOtpActivated(false)
    setOtp('')
    onCancel()
  }

  const onSuccess = () => {
    toast.success({
      title: t('toast_success_title'),
      subTitle: t('toast_success_message')
    })
    handleCLoseModal()
    onSuccessToggle()
  }

  const onError = () => {
    setOtp('')
    setIsError(true)
  }

  const isOtpFilled = otp.length > 5

  const {
    query: { data, isFetching }
  } = useGetQrCode(isOpen)

  const {
    mutation: { mutate: enableTwoFactorMutate, isPending }
  } = useEnableTwoFactor({ onSuccess, onError })

  const onConfirm = useCallback(() => {
    enableTwoFactorMutate({ otp: otp.toString(), secret: data!.secret })
  }, [otp, data, enableTwoFactorMutate])

  const disableSubmit = !data || !isOtpFilled || isPending
  const disableInput = !data || isPending

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCLoseModal}
      setIsOpen={setIsOpen}
      zIndex={1003}
      maxWidth={1200}
      maxHeight={770}
      layers={[
        {
          icon: LockShieldLineIcon,
          title: t('header'),
          bodyPaddings: false,
          body: (
            <div className={style.wrapper}>
              <div className={style.content}>
                <div className={style.leftItem}>
                  <div className={style.main}>
                    <div className={style.title}>{t('item1_title')}</div>
                    <div>
                      <span>{t('item1_text')}</span>
                      <ul>
                        <li>{t('item1_authenticator_microsoft')}</li>
                        <li>{t('item1_authenticator_google')}</li>
                      </ul>
                    </div>
                    <span className={style.badgeTitle}>
                      {t('item1_badge_title')}
                    </span>
                    <div className={style.badgeContainer}>
                      <img alt={'apple'} src={apple} />
                      <img alt={'google'} src={google} />
                    </div>
                  </div>
                  <img alt={''} src={svg1} width={170} height={148} />
                </div>
                <div className={style.qrItem}>
                  <div className={style.title}>
                    <QrCodeViewfinderLineIcon size={IconSizes.Size24} />{' '}
                    <span>{t('item2_title')}</span>
                  </div>
                  <div className={style.main}>
                    {isFetching ? (
                      <Spinner />
                    ) : (
                      <img alt={'qr'} src={data?.qr} />
                    )}{' '}
                    <span>{t('item2_text')}</span>
                  </div>
                </div>
                <div className={style.leftItem}>
                  <div className={style.main}>
                    <div className={style.title}>{t('item3_title')}</div>
                    <div>
                      <span>{t('item3_text')}</span>
                    </div>
                  </div>
                  <img alt={''} src={svg2} />
                </div>
                <div className={style.activateItem}>
                  <div
                    className={cn(
                      style.title,
                      otpActivated ? style.success : undefined
                    )}
                  >
                    <CheckmarkIcon size={IconSizes.Size24} />
                    <span>{t('item4_title')}</span>
                  </div>
                  <span>{t('item4_label')}</span>
                  <OtpInput
                    value={otp}
                    onChange={setOtp}
                    disabled={disableInput}
                    onComplete={disableSubmit ? () => null : onConfirm}
                  />
                  <div className={style.buttonsContainer}>
                    {isError ? (
                      <span className={style.errorMessage}>
                        {t('error_message')}
                      </span>
                    ) : null}
                    {otpActivated ? (
                      <span className={style.successMessage}>
                        {t('success_message')}
                      </span>
                    ) : (
                      <Button
                        variant={ButtonVariants.Dark}
                        disabled={disableSubmit}
                        onClick={onConfirm}
                        label={'Confirm'}
                        type={'submit'}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ]}
    />
  )
}
